// ./src/hooks/useEmailLayouts.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  EmailLayout,
  EmailLayoutService,
  EmailLayoutFilter,
  EmailLayoutInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseEmailLayoutsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<EmailLayoutFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** User ID for change detection */
  userId?: string | null;
}

export interface UseEmailLayoutsReturn {
  // Data state
  emailLayout: EmailLayout | null;
  emailLayouts: EmailLayout[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Individual item loading state
  loadingEmailLayoutIds: string[];
  isLoadingEmailLayout: (id: string) => boolean;

  // Search and filters
  filters: Partial<EmailLayoutFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isPreviewing: boolean;
  isPublishing: boolean;
  removingEmailLayoutId: string | null;
  selectedEmailLayout: EmailLayout | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<EmailLayout | null>;
  list: (
    filter?: Partial<EmailLayoutFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<EmailLayout[]>;
  create: (params: EmailLayoutInput) => Promise<EmailLayout | null>;
  update: (id: string, params: EmailLayoutInput) => Promise<EmailLayout | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;
  preview: (id: string, sampleData?: string) => Promise<EmailLayout | null>;
  publish: (id: string) => Promise<EmailLayout | null>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<EmailLayoutFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedEmailLayout: (emailLayout: EmailLayout | null) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useEmailLayouts = (
  options: UseEmailLayoutsOptions = {}
): UseEmailLayoutsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [emailLayout, setEmailLayout] = useState<EmailLayout | null>(null);
  const [emailLayouts, setEmailLayouts] = useState<EmailLayout[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Individual item loading state - tracks IDs currently being loaded
  const [loadingEmailLayoutIds, setLoadingEmailLayoutIds] = useState<string[]>(
    []
  );

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<EmailLayoutFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [removingEmailLayoutId, setRemovingEmailLayoutId] = useState<
    string | null
  >(null);
  const [selectedEmailLayout, setSelectedEmailLayout] =
    useState<EmailLayout | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => emailLayouts.length === 0 && !loading,
    [emailLayouts.length, loading]
  );

  // Helper to check if a specific email layout is loading
  const isLoadingEmailLayout = useCallback(
    (id: string) => loadingEmailLayoutIds.includes(id),
    [loadingEmailLayoutIds]
  );

  // Helper to add ID to loading set
  const addLoadingId = useCallback((id: string) => {
    setLoadingEmailLayoutIds((prev) =>
      prev.includes(id) ? prev : [...prev, id]
    );
  }, []);

  // Helper to remove ID from loading set
  const removeLoadingId = useCallback((id: string) => {
    setLoadingEmailLayoutIds((prev) =>
      prev.filter((loadingId) => loadingId !== id)
    );
  }, []);

  // Helper to add multiple IDs to loading set
  const addLoadingIds = useCallback((ids: string[]) => {
    setLoadingEmailLayoutIds((prev) => {
      const newIds = ids.filter((id) => !prev.includes(id));
      return newIds.length > 0 ? [...prev, ...newIds] : prev;
    });
  }, []);

  // Helper to remove multiple IDs from loading set
  const removeLoadingIds = useCallback((ids: string[]) => {
    setLoadingEmailLayoutIds((prev) =>
      prev.filter((loadingId) => !ids.includes(loadingId))
    );
  }, []);

  // Helper function to update email layout in list if it exists, or add it if not
  const updateOrAddEmailLayoutInList = useCallback(
    (updatedEmailLayout: EmailLayout) => {
      setEmailLayouts((prev) => {
        const existingIndex = prev.findIndex(
          (l) => l.id === updatedEmailLayout.id
        );
        if (existingIndex >= 0) {
          const newEmailLayouts = [...prev];
          newEmailLayouts[existingIndex] = updatedEmailLayout;
          return newEmailLayouts;
        } else {
          return [updatedEmailLayout, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove email layout from list if it exists
  const removeEmailLayoutFromList = useCallback((emailLayoutId: string) => {
    setEmailLayouts((prev) => prev.filter((l) => l.id !== emailLayoutId));
  }, []);

  // Helper function to remove multiple email layouts from list
  const removeEmailLayoutsFromList = useCallback((emailLayoutIds: string[]) => {
    const idsSet = new Set(emailLayoutIds);
    setEmailLayouts((prev) => prev.filter((l) => !idsSet.has(l.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setEmailLayout(null);
    setEmailLayouts([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsPreviewing(false);
    setIsPublishing(false);
    setRemovingEmailLayoutId(null);
    setSelectedEmailLayout(null);
    setLoadingEmailLayoutIds([]);
  }, []);

  // Get single email layout (matching API method name)
  const get = useCallback(
    async (id: string): Promise<EmailLayout | null> => {
      addLoadingId(id);
      setLastError(null);

      try {
        const result = await EmailLayoutService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const emailLayoutData = result.data[0] as EmailLayout;
          setEmailLayout(emailLayoutData);
          updateOrAddEmailLayoutInList(emailLayoutData);
          setLastError(null);
          return emailLayoutData;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load email layout";
          setLastError(errorMessage);
          setEmailLayout(null);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load email layout";
        setLastError(errorMessage);
        setEmailLayout(null);
        return null;
      } finally {
        removeLoadingId(id);
      }
    },
    [updateOrAddEmailLayoutInList, addLoadingId, removeLoadingId]
  );

  // List email layouts (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<EmailLayoutFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setEmailLayouts([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await EmailLayoutService.list(
          filter as EmailLayoutFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEmailLayouts = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setEmailLayouts((prev) => {
              const existingIds = new Set(prev.map((l) => l.id));
              const uniqueNewEmailLayouts = newEmailLayouts.filter(
                (l: any) => !existingIds.has(l.id)
              );
              return [...prev, ...uniqueNewEmailLayouts];
            });
          } else {
            setEmailLayouts(newEmailLayouts);
          }

          setHasMore(newEmailLayouts.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load email layouts";
          setLastError(errorMessage);
          if (!isAppending) setEmailLayouts([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load email layouts";
        setLastError(errorMessage);
        if (!isAppending) setEmailLayouts([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many email layouts by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<EmailLayout[]> => {
      if (ids.length === 0) {
        return [];
      }

      addLoadingIds(ids);
      setLastError(null);

      try {
        const result = await EmailLayoutService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedEmailLayouts = Array.isArray(result.data)
            ? result.data
            : [];
          loadedEmailLayouts.forEach((emailLayout: any) => {
            updateOrAddEmailLayoutInList(emailLayout);
          });
          setLastError(null);
          return loadedEmailLayouts;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load email layouts";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load email layouts";
        setLastError(errorMessage);
        return [];
      } finally {
        removeLoadingIds(ids);
      }
    },
    [updateOrAddEmailLayoutInList, addLoadingIds, removeLoadingIds]
  );

  // Create email layout (matching API method name)
  const create = useCallback(
    async (params: EmailLayoutInput): Promise<EmailLayout | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await EmailLayoutService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEmailLayout = result.data[0] as EmailLayout;
          setEmailLayout(newEmailLayout);
          setEmailLayouts((prev) => [newEmailLayout, ...prev]);
          setLastError(null);
          return newEmailLayout;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create email layout";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create email layout";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update email layout (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: EmailLayoutInput
    ): Promise<EmailLayout | null> => {
      if (!id) {
        setLastError("Email layout ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await EmailLayoutService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedEmailLayout = result.data[0] as EmailLayout;
          setEmailLayout(updatedEmailLayout);
          updateOrAddEmailLayoutInList(updatedEmailLayout);
          setLastError(null);
          return updatedEmailLayout;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update email layout";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update email layout";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddEmailLayoutInList]
  );

  // Publish email layout (matching API method name)
  const publish = useCallback(
    async (id: string): Promise<EmailLayout | null> => {
      if (!id) {
        setLastError("Email layout ID is required for published");
        return null;
      }

      setIsPublishing(true);
      setLastError(null);

      try {
        const result = await EmailLayoutService.publish({ id });

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedEmailLayout = result.data[0] as EmailLayout;
          setEmailLayout(updatedEmailLayout);
          updateOrAddEmailLayoutInList(updatedEmailLayout);
          setLastError(null);
          return updatedEmailLayout;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to publish email layout";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to publish email layout";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsPublishing(false);
      }
    },
    [updateOrAddEmailLayoutInList]
  );

  // Remove email layout (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Email layout ID is required for removal");
        return false;
      }

      setRemovingEmailLayoutId(id);
      setLastError(null);

      try {
        const result = await EmailLayoutService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setEmailLayout(null);
          removeEmailLayoutFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove email layout";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove email layout";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingEmailLayoutId(null);
      }
    },
    [removeEmailLayoutFromList]
  );

  // Remove many email layouts (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one email layout ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await EmailLayoutService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setEmailLayout(null);
          removeEmailLayoutsFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove email layouts";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove email layouts";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeEmailLayoutsFromList]
  );

  // Preview email layout
  const preview = useCallback(
    async (id: string, sampleData?: string): Promise<EmailLayout | null> => {
      if (!id) {
        setLastError("Email layout ID is required for preview");
        return null;
      }

      setIsPreviewing(true);
      setLastError(null);

      try {
        const result = await EmailLayoutService.preview(id, sampleData);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const previewEmailLayout = result.data[0] as EmailLayout;
          setLastError(null);
          return previewEmailLayout;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to preview email layout";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to preview email layout";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsPreviewing(false);
      }
    },
    []
  );

  // Load more email layouts
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh email layouts
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  const prevUserIdRef = useRef(userId);

  useEffect(() => {
    // Reset when user changes
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      setHasAutoLoaded(false);
      reset();
    }

    if (autoLoad && userId && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, userId, hasAutoLoaded, list, pageSize, initialSort, reset]);

  return {
    emailLayout,
    emailLayouts,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    loadingEmailLayoutIds,
    isLoadingEmailLayout,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    isPreviewing,
    isPublishing,
    removingEmailLayoutId,
    selectedEmailLayout,
    get,
    list,
    getMany,
    create,
    update,
    publish,
    remove,
    removeMany,
    preview,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedEmailLayout,
    reset,
    lastError,
    clearError,
  };
};
